import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Custom drag and drop hook for list reordering.
 * Supports mouse and touch via Pointer Events API.
 * Works for both vertical and horizontal lists.
 *
 * @param {Object} options - Configuration options
 * @param {Array|number} options.items - Array of items or item count
 * @param {Function} options.onReorder - Callback when items are reordered: (fromIndex, toIndex) => void
 * @param {Function} [options.onDragStart] - Optional callback when drag starts
 * @param {Function} [options.onDragEnd] - Optional callback when drag ends
 * @param {boolean} [options.isDisabled=false] - Disable all drag functionality
 * @param {'vertical'|'horizontal'} [options.orientation='vertical'] - Drag direction
 *
 * @returns {Object} Hook API
 * @returns {Object} containerProps - Props to spread on the container element
 * @returns {Function} getItemProps - Function to get props for each draggable item
 * @returns {Function} getDragHandleProps - Function to get props for the drag handle
 * @returns {boolean} isDragging - Whether an item is currently being dragged
 * @returns {number} draggedIndex - Index of the item being dragged (-1 if none)
 * @returns {number} targetIndex - Index where the item will be dropped (-1 if none)
 */
const useDnD = ({
    items,
    onReorder,
    onDragStart,
    onDragEnd,
    isDisabled = false,
    orientation = 'vertical',
}) => {
    // ========================================
    // STATE
    // ========================================
    const [draggedIndex, setDraggedIndex] = useState(-1);
    const [targetIndex, setTargetIndex] = useState(-1);

    // ========================================
    // REFS
    // ========================================
    const containerRef = useRef(null);
    const itemRefs = useRef([]);
    const dragStateRef = useRef({
        isDragging: false,
        draggedIdx: -1,
        startPos: 0,
        currentPos: 0,
        startScroll: 0,
        startContainerPos: 0,
        scrollInterval: null,
        justFinishedDragging: false,
        justFinishedTimeoutId: null,
        // Cached measurements for performance
        itemSizes: [],
        itemOffsets: [],
        rafId: null,
    });

    // ========================================
    // DERIVED VALUES
    // ========================================
    const itemCount = useMemo(() => (Array.isArray(items) ? items.length : items), [items]);
    const isHorizontal = orientation === 'horizontal';

    const isDragging = draggedIndex !== -1;
    const isSingleItem = itemCount <= 1;
    const effectivelyDisabled = isDisabled || isSingleItem;

    // ========================================
    // UTILITY FUNCTIONS
    // ========================================

    // Get the appropriate client position based on orientation
    const getClientPos = useCallback(
        (event) => (isHorizontal ? event.clientX : event.clientY),
        [isHorizontal],
    );

    // Cache item measurements at drag start for performance
    const cacheItemMeasurements = useCallback(() => {
        const elements = itemRefs.current;
        const sizes = [];
        const offsets = [];
        const gap = 8; // margin/gap between items
        const firstRect = elements.find(Boolean)?.getBoundingClientRect();
        const origin = firstRect ? (isHorizontal ? firstRect.left : firstRect.top) : 0;

        for (let i = 0; i < elements.length; i++) {
            const el = elements[i];
            if (el) {
                const size = (isHorizontal ? el.offsetWidth : el.offsetHeight) + gap;
                const rect = el.getBoundingClientRect();
                sizes.push(size);
                offsets.push((isHorizontal ? rect.left : rect.top) - origin);
            } else {
                sizes.push(60); // Fallback size
                offsets.push(0);
            }
        }

        dragStateRef.current.itemSizes = sizes;
        dragStateRef.current.itemOffsets = offsets;
    }, [isHorizontal]);

    const getDragOffset = useCallback(
        (clientPos) => {
            const container = containerRef.current;
            if (!container) return 0;
            const rect = container.getBoundingClientRect();
            const scroll = isHorizontal ? container.scrollLeft : container.scrollTop;
            const containerPos = isHorizontal ? rect.left : rect.top;
            const state = dragStateRef.current;
            return (
                clientPos -
                state.startPos +
                scroll -
                state.startScroll +
                state.startContainerPos -
                containerPos
            );
        },
        [isHorizontal],
    );

    const calculateTargetIndex = useCallback(
        (clientPos) => {
            const { itemSizes, itemOffsets, draggedIdx: draggedIndex } = dragStateRef.current;
            if (itemSizes.length === 0 || draggedIndex === -1) return draggedIndex;

            const draggedSize = itemSizes[draggedIndex] || 60;
            const draggedOriginalOffset = itemOffsets[draggedIndex] || 0;

            // Calculate the current position of the dragged element's center
            const dragOffset = getDragOffset(clientPos);
            const draggedCurrentCenter = draggedOriginalOffset + draggedSize / 2 + dragOffset;

            let newTargetIndex = draggedIndex;

            for (let i = 0; i < itemSizes.length; i++) {
                if (i === draggedIndex) continue;

                const itemOffset = itemOffsets[i];
                const itemSize = itemSizes[i];
                const itemCenter = itemOffset + itemSize / 2;

                // When dragging forward (down/right): swap when dragged element's center passes the target's center
                if (i > draggedIndex && draggedCurrentCenter > itemCenter) {
                    newTargetIndex = i;
                }

                // When dragging backward (up/left): swap when dragged element's center passes the target's center
                if (i < draggedIndex && draggedCurrentCenter < itemCenter) {
                    newTargetIndex = i;
                    break; // Take the first item we pass
                }
            }

            // Clamp to valid range
            return Math.min(Math.max(0, newTargetIndex), itemCount - 1);
        },
        [getDragOffset, itemCount],
    );

    const updateDragPosition = useCallback(
        (clientPos) => {
            if (!dragStateRef.current.isDragging) return;
            dragStateRef.current.currentPos = clientPos;
            const element = itemRefs.current[dragStateRef.current.draggedIdx];
            if (element) {
                const translate = getDragOffset(clientPos);
                element.style.transform = isHorizontal
                    ? `translate3d(${translate}px, 0, 0)`
                    : `translate3d(0, ${translate}px, 0)`;
            }
            const newTargetIndex = calculateTargetIndex(clientPos);
            setTargetIndex((prev) => (prev !== newTargetIndex ? newTargetIndex : prev));
        },
        [calculateTargetIndex, getDragOffset, isHorizontal],
    );

    const handleAutoScroll = useCallback(
        (clientPos) => {
            const container = containerRef.current;
            if (!container) return;

            const containerRect = container.getBoundingClientRect();
            const scrollThreshold = 50;
            const scrollSpeed = 10;

            // Clear existing scroll interval
            if (dragStateRef.current.scrollInterval) {
                clearInterval(dragStateRef.current.scrollInterval);
                dragStateRef.current.scrollInterval = null;
            }

            if (isHorizontal) {
                // Horizontal scrolling
                const distanceFromLeft = clientPos - containerRect.left;
                const distanceFromRight = containerRect.right - clientPos;

                if (distanceFromLeft < scrollThreshold && container.scrollLeft > 0) {
                    dragStateRef.current.scrollInterval = setInterval(() => {
                        container.scrollLeft -= scrollSpeed;
                    }, 16);
                } else if (
                    distanceFromRight < scrollThreshold &&
                    container.scrollLeft < container.scrollWidth - container.clientWidth
                ) {
                    dragStateRef.current.scrollInterval = setInterval(() => {
                        container.scrollLeft += scrollSpeed;
                    }, 16);
                }
            } else {
                // Vertical scrolling
                const distanceFromTop = clientPos - containerRect.top;
                const distanceFromBottom = containerRect.bottom - clientPos;

                if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
                    dragStateRef.current.scrollInterval = setInterval(() => {
                        container.scrollTop -= scrollSpeed;
                    }, 16);
                } else if (
                    distanceFromBottom < scrollThreshold &&
                    container.scrollTop < container.scrollHeight - container.clientHeight
                ) {
                    dragStateRef.current.scrollInterval = setInterval(() => {
                        container.scrollTop += scrollSpeed;
                    }, 16);
                }
            }
        },
        [isHorizontal],
    );

    const clearAutoScroll = useCallback(() => {
        if (dragStateRef.current.scrollInterval) {
            clearInterval(dragStateRef.current.scrollInterval);
            dragStateRef.current.scrollInterval = null;
        }
    }, []);

    // ========================================
    // DRAG HANDLERS (POINTER EVENTS)
    // ========================================
    const startDrag = useCallback(
        (index, clientPos) => {
            if (effectivelyDisabled) return;

            // Cache measurements at drag start
            cacheItemMeasurements();

            dragStateRef.current.isDragging = true;
            dragStateRef.current.draggedIdx = index;
            dragStateRef.current.startPos = clientPos;
            dragStateRef.current.currentPos = clientPos;
            const container = containerRef.current;
            const rect = container.getBoundingClientRect();
            dragStateRef.current.startScroll = isHorizontal
                ? container.scrollLeft
                : container.scrollTop;
            dragStateRef.current.startContainerPos = isHorizontal ? rect.left : rect.top;

            setDraggedIndex(index);
            setTargetIndex(index);

            onDragStart?.({ source: { index } });
        },
        [cacheItemMeasurements, effectivelyDisabled, isHorizontal, onDragStart],
    );

    const updateDrag = useCallback(
        (clientPos) => {
            if (!dragStateRef.current.isDragging) return;

            updateDragPosition(clientPos);
            handleAutoScroll(clientPos);
        },
        [handleAutoScroll, updateDragPosition],
    );

    const endDrag = useCallback(
        (cancelled = false) => {
            if (!dragStateRef.current.isDragging && draggedIndex === -1) return;

            clearAutoScroll();

            // Cancel any pending RAF
            if (dragStateRef.current.rafId) {
                cancelAnimationFrame(dragStateRef.current.rafId);
                dragStateRef.current.rafId = null;
            }

            // Reset the dragged element's transform
            const draggedElement = itemRefs.current[dragStateRef.current.draggedIdx];
            if (draggedElement) {
                draggedElement.style.transform = '';
            }

            const fromIndex = dragStateRef.current.draggedIdx;
            const toIndex = cancelled
                ? fromIndex
                : calculateTargetIndex(dragStateRef.current.currentPos);

            dragStateRef.current.isDragging = false;
            dragStateRef.current.draggedIdx = -1;
            dragStateRef.current.justFinishedDragging = true;
            dragStateRef.current.itemSizes = [];
            dragStateRef.current.itemOffsets = [];

            // Clear any existing timeout
            if (dragStateRef.current.justFinishedTimeoutId) {
                clearTimeout(dragStateRef.current.justFinishedTimeoutId);
            }

            // Clear the flag after a short delay to prevent click events
            dragStateRef.current.justFinishedTimeoutId = setTimeout(() => {
                dragStateRef.current.justFinishedDragging = false;
                dragStateRef.current.justFinishedTimeoutId = null;
            }, 100);

            onDragEnd?.({
                source: { index: fromIndex },
                destination: cancelled ? null : { index: toIndex },
            });

            if (!cancelled && fromIndex !== toIndex && fromIndex !== -1 && toIndex !== -1) {
                onReorder?.(fromIndex, toIndex);
            }

            setDraggedIndex(-1);
            setTargetIndex(-1);
        },
        [calculateTargetIndex, clearAutoScroll, draggedIndex, onDragEnd, onReorder],
    );

    // ========================================
    // POINTER EVENT HANDLERS
    // ========================================
    const handlePointerDown = useCallback(
        (index, event) => {
            if (effectivelyDisabled) return;
            if (event.button !== 0) return; // Only left mouse button

            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);

            startDrag(index, getClientPos(event));
        },
        [effectivelyDisabled, getClientPos, startDrag],
    );

    const handlePointerMove = useCallback(
        (event) => {
            if (!dragStateRef.current.isDragging) return;
            event.preventDefault();

            const clientPos = getClientPos(event);

            // Immediately update dragged element position for smooth following
            dragStateRef.current.currentPos = clientPos;
            const draggedElement = itemRefs.current[dragStateRef.current.draggedIdx];
            if (draggedElement) {
                const translate = getDragOffset(clientPos);
                draggedElement.style.transform = isHorizontal
                    ? `translate3d(${translate}px, 0, 0)`
                    : `translate3d(0, ${translate}px, 0)`;
            }

            // Use RAF to throttle target index calculation (for other items)
            if (dragStateRef.current.rafId) {
                cancelAnimationFrame(dragStateRef.current.rafId);
            }

            dragStateRef.current.rafId = requestAnimationFrame(() => {
                updateDrag(clientPos);
                dragStateRef.current.rafId = null;
            });
        },
        [getClientPos, getDragOffset, isHorizontal, updateDrag],
    );

    const handlePointerUp = useCallback(
        (event) => {
            if (!dragStateRef.current.isDragging) return;
            event.preventDefault();
            event.currentTarget.releasePointerCapture(event.pointerId);
            dragStateRef.current.currentPos = getClientPos(event);
            endDrag(false);
        },
        [endDrag, getClientPos],
    );

    const handlePointerCancel = useCallback(
        (event) => {
            if (!dragStateRef.current.isDragging) return;
            event.currentTarget.releasePointerCapture(event.pointerId);
            endDrag(true);
        },
        [endDrag],
    );

    // ========================================
    // CLEANUP EFFECT
    // ========================================
    useEffect(() => {
        const dragState = dragStateRef.current;
        return () => {
            clearAutoScroll();
            if (dragState.rafId) {
                cancelAnimationFrame(dragState.rafId);
            }
            if (dragState.justFinishedTimeoutId) {
                clearTimeout(dragState.justFinishedTimeoutId);
            }
        };
    }, [clearAutoScroll]);

    // Clean up stale item refs when item count changes
    useEffect(() => {
        if (itemRefs.current.length > itemCount) {
            itemRefs.current = itemRefs.current.slice(0, itemCount);
        }
    }, [itemCount]);

    // Keep the pointer position stable when the list or an ancestor scrolls.
    useEffect(() => {
        if (!isDragging) return;
        const handleScroll = () => updateDragPosition(dragStateRef.current.currentPos);
        document.addEventListener('scroll', handleScroll, true);
        return () => document.removeEventListener('scroll', handleScroll, true);
    }, [isDragging, updateDragPosition]);

    // Cancel drag on escape key (global)
    useEffect(() => {
        if (!isDragging) return;

        const handleGlobalKeyDown = (event) => {
            if (event.key === 'Escape') {
                endDrag(true);
            }
        };

        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
            document.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [endDrag, isDragging]);

    // ========================================
    // API: CONTAINER PROPS
    // ========================================
    const containerProps = useMemo(
        () => ({
            ref: containerRef,
            style: {
                position: 'relative',
            },
        }),
        [],
    );

    // ========================================
    // API: GET ITEM PROPS
    // ========================================
    const getItemProps = useCallback(
        (index) => {
            const isBeingDragged = draggedIndex === index;

            // Calculate visual offset for non-dragged items during drag
            // The dragged item's transform is handled directly via DOM manipulation for performance
            let translate = 0;
            if (isDragging && draggedIndex !== -1 && !isBeingDragged) {
                // Use cached size for displacement
                const draggedSize = dragStateRef.current.itemSizes[draggedIndex] || 60;
                if (draggedIndex < index && targetIndex >= index) {
                    // Items after the dragged item that need to move backward
                    translate = -draggedSize;
                } else if (draggedIndex > index && targetIndex <= index) {
                    // Items before the dragged item that need to move forward
                    translate = draggedSize;
                }
            }

            const transform =
                translate !== 0
                    ? isHorizontal
                        ? `translate3d(${translate}px, 0, 0)`
                        : `translate3d(0, ${translate}px, 0)`
                    : undefined;

            return {
                ref: (el) => {
                    itemRefs.current[index] = el;
                },
                onClickCapture: (e) => {
                    // Prevent any click events during or right after dragging
                    if (isDragging || dragStateRef.current.justFinishedDragging) {
                        e.stopPropagation();
                        e.preventDefault();
                    }
                },
                style: {
                    position: 'relative',
                    userSelect: 'none',
                    marginBottom: isHorizontal ? undefined : 8,
                    marginRight: isHorizontal ? 8 : undefined,
                    cursor: effectivelyDisabled ? 'default' : 'auto',
                    transform,
                    // Smooth transition for displaced items, none for the dragged item
                    transition:
                        isDragging && !isBeingDragged ? 'transform 200ms ease-out' : undefined,
                    willChange: isDragging ? 'transform' : undefined,
                    zIndex: isBeingDragged ? 9999 : 1,
                },
                'data-dragging': isBeingDragged || undefined,
            };
        },
        [draggedIndex, effectivelyDisabled, isDragging, isHorizontal, targetIndex],
    );

    // ========================================
    // API: GET DRAG HANDLE PROPS
    // ========================================
    const getDragHandleProps = useCallback(
        (index) => {
            if (effectivelyDisabled) {
                return {
                    style: {
                        cursor: 'not-allowed',
                        opacity: 0.5,
                    },
                };
            }

            return {
                onPointerDown: (e) => handlePointerDown(index, e),
                onPointerMove: handlePointerMove,
                onPointerUp: handlePointerUp,
                onPointerCancel: handlePointerCancel,
                onClick: (e) => {
                    // Prevent click from bubbling up and triggering parent handlers
                    e.stopPropagation();
                    e.preventDefault();
                },
                onMouseDown: (e) => {
                    // Prevent mousedown from triggering parent handlers
                    e.stopPropagation();
                },
                style: {
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none', // Prevent browser gestures during drag
                },
            };
        },
        [
            effectivelyDisabled,
            handlePointerCancel,
            handlePointerDown,
            handlePointerMove,
            handlePointerUp,
            isDragging,
        ],
    );

    // ========================================
    // RETURN API
    // ========================================
    return {
        containerProps,
        getItemProps,
        getDragHandleProps,
        isDragging,
        draggedIndex,
        targetIndex,
    };
};

export default useDnD;
